import { apiClient } from "@api/client";
import {
  ClinicAuthError,
  clinicAuthClient,
  type ClinicSession,
} from "@auth/clinicAuthClient";
import { getActiveSession, useSessionStore } from "@auth/sessionStore";

// A screen authenticates by its display cookie alone, never a staff session.
const DISPLAY_SCREEN = [
  "/display/me",
  "/display/pairing",
  "/display/pairing/token",
];

// The `/health/live` probe must not drag a token refresh onto a wire that is broken.
const SKIP_INJECT = new Set([
  "/auth/login",
  "/auth/refresh",
  "/health/live",
  ...DISPLAY_SCREEN,
]);
const SKIP_REFRESH = new Set([
  "/auth/login",
  "/auth/refresh",
  "/auth/logout",
  "/auth/select-user",
  "/health/live",
  ...DISPLAY_SCREEN,
]);

const EXPIRY_LEEWAY_MS = 30_000;

let refreshing: Promise<ClinicSession | null> | null = null;

export const refreshOnce = async (): Promise<ClinicSession | null> => {
  if (refreshing) return refreshing;
  const current = getActiveSession();
  if (!current) return null;
  refreshing = (async () => {
    try {
      const rotated = await clinicAuthClient.refresh(current.refreshToken);
      useSessionStore.getState().rotate(rotated);
      return rotated;
    } catch (err) {
      if (
        err instanceof ClinicAuthError &&
        err.code === "invalid_credentials"
      ) {
        useSessionStore.getState().clearAll();
      }
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
};

const isExpiringSoon = (s: ClinicSession): boolean =>
  s.accessExpiresAt - Date.now() < EXPIRY_LEEWAY_MS;

export const ensureFreshSession = async (): Promise<ClinicSession | null> => {
  const active = getActiveSession();
  if (!active) return null;
  if (!isExpiringSoon(active)) return active;
  return (await refreshOnce()) ?? active;
};

export const installAuthMiddleware = () => {
  const retryables = new Map<string, Request>();

  apiClient.use({
    async onRequest({ request, schemaPath, id }) {
      if (SKIP_INJECT.has(schemaPath)) return;

      let active = getActiveSession();
      if (active && isExpiringSoon(active)) {
        active = (await refreshOnce()) ?? active;
      }
      if (active && !request.headers.has("Authorization")) {
        request.headers.set("Authorization", `Bearer ${active.accessToken}`);
      }
      if (!SKIP_REFRESH.has(schemaPath)) {
        retryables.set(id, request.clone());
      }
    },
    async onResponse({ response, schemaPath, id }) {
      const retry = retryables.get(id);
      retryables.delete(id);
      if (response.status !== 401) return;
      if (SKIP_REFRESH.has(schemaPath)) return;
      if (!retry) return;
      const rotated = await refreshOnce();
      if (!rotated) return;
      retry.headers.set("Authorization", `Bearer ${rotated.accessToken}`);
      return fetch(retry);
    },
    onError({ id }) {
      retryables.delete(id);
    },
  });
};
